import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useQuery, useMutation } from '@apollo/client'
import { useTranslation } from 'react-i18next'
import {
    PlusIcon,
    PencilIcon,
    TrashIcon,
    MagnifyingGlassIcon,
    ArrowLeftIcon,
    TicketIcon,
    EyeIcon,
    EllipsisVerticalIcon,
} from '@heroicons/react/20/solid'
import { Button } from '../../components/button'
import { Input, InputGroup } from '../../components/input'
import { Select } from '../../components/select'
import { Badge } from '../../components/badge'
import { Divider } from '../../components/divider'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/table'
import { Dropdown, DropdownButton, DropdownDivider, DropdownItem, DropdownLabel, DropdownMenu } from '../../components/dropdown'
import { ErrorDialog, SuccessDialog, ConfirmDialog } from '../../components/custom/ResponseDialog'
import { Pagination, PaginationList, PaginationNext, PaginationPage, PaginationPrevious } from '../../components/pagination'
import { GET_SUBSCRIPTION_PLANS } from '../../graphql/subscription/queries'
import { ACTIVATE_SUBSCRIPTION_PLAN, DEACTIVATE_SUBSCRIPTION_PLAN } from '../../graphql/subscription/mutations'
import { getPageRange } from '../../utils/paginationHelper'
import loadingImage from '../../assets/images/boat-loading.gif'

const SubscriptionManagement = () => {
    const { t } = useTranslation()
    const navigate = useNavigate()
    const [searchString, setSearchString] = useState('')
    const [statusFilter, setStatusFilter] = useState('')
    const [categoryFilter, setCategoryFilter] = useState('')
    const [activateDialog, setActivateDialog] = useState({ open: false, id: null, name: '' })
    const [deactivateDialog, setDeactivateDialog] = useState({ open: false, id: null, name: '' })
    const [successDialog, setSuccessDialog] = useState({ open: false, message: '' })
    const [errorDialog, setErrorDialog] = useState({ open: false, message: '' })
    const [showErrDialog, setShowErrDialog] = useState(false)
    const [showSucDialog, setShowSucDialog] = useState(false)

    const [page, setPage] = useState(0)
    const [rowsPerPage, setRowsPerPage] = useState(10)

    const { data, loading, error, refetch } = useQuery(GET_SUBSCRIPTION_PLANS, {
        variables: {
            filter: {
                status_code: statusFilter || null,
                category_code: categoryFilter || null,
            },
            page: {
                offset: page * rowsPerPage,
                limit: rowsPerPage,
            },
        },
        fetchPolicy: 'network-only',
    })

    const [activateSubscription, { loading: activateLoading }] = useMutation(ACTIVATE_SUBSCRIPTION_PLAN, {
        onCompleted: data => {
            if (data?.activateSubscriptionPlan?.success) {
                setSuccessDialog({
                    open: true,
                    message: data.activateSubscriptionPlan.message || 'Subscription plan activated successfully',
                })
                setShowSucDialog(true)
                refetch()
            } else {
                setErrorDialog({
                    open: true,
                    message: data?.activateSubscriptionPlan?.message || 'Failed to activate subscription plan',
                })
                setShowErrDialog(true)
            }
            setActivateDialog({ open: false, id: null, name: '' })
        },
        onError: error => {
            setErrorDialog({
                open: true,
                message: error.message || 'An error occurred while activating subscription plan',
            })
            setShowErrDialog(true)
            setActivateDialog({ open: false, id: null, name: '' })
        },
    })

    const [deactivateSubscription, { loading: deactivateLoading }] = useMutation(DEACTIVATE_SUBSCRIPTION_PLAN, {
        onCompleted: data => {
            if (data?.deactivateSubscriptionPlan?.success) {
                setSuccessDialog({
                    open: true,
                    message: data.deactivateSubscriptionPlan.message || 'Subscription plan deactivated successfully',
                })
                setShowSucDialog(true)
                refetch()
            } else {
                setErrorDialog({
                    open: true,
                    message: data?.deactivateSubscriptionPlan?.message || 'Failed to deactivate subscription plan',
                })
                setShowErrDialog(true)
            }
            setDeactivateDialog({ open: false, id: null, name: '' })
        },
        onError: error => {
            setErrorDialog({
                open: true,
                message: error.message || 'An error occurred while deactivating subscription plan',
            })
            setShowErrDialog(true)
            setDeactivateDialog({ open: false, id: null, name: '' })
        },
    })

    const subscriptions = data?.getSubscriptionPlans?.subscription_plans || []
    const totalCount = data?.getSubscriptionPlans?.total_count || 0
    const totalPages = Math.ceil(totalCount / rowsPerPage)

    const filteredSubscriptions = subscriptions.filter(sub => {
        const matchesSearch =
            searchString === '' ||
            sub.subscription_name?.en?.toLowerCase().includes(searchString.toLowerCase()) ||
            sub.subscription_name?.ar?.includes(searchString)

        return matchesSearch
    })

    const onClickSearch = () => {
        setPage(0)
    }

    const onClickSearchClear = () => {
        setSearchString('')
        setStatusFilter('')
        setCategoryFilter('')
        setPage(0)
    }

    const handlePageChange = newPage => {
        setPage(newPage)
    }

    const getStatusBadge = statusCode => {
        const statusMap = {
            ACT: { color: 'emerald', label: 'Active' },
            DRF: { color: 'zinc', label: 'Draft' },
            EXP: { color: 'amber', label: 'Expired' },
            INA: { color: 'rose', label: 'Inactive' },
        }
        const status = statusMap[statusCode] || { color: 'zinc', label: statusCode }
        return <Badge color={status.color}>{t(status.label)}</Badge>
    }

    const getCategoryBadge = categoryCode => {
        const categoryMap = {
            DMC: { color: 'blue', label: 'Domestic' },
            INL: { color: 'purple', label: 'International' },
        }
        const category = categoryMap[categoryCode] || { color: 'zinc', label: categoryCode }
        return <Badge color={category.color}>{t(category.label)}</Badge>
    }

    const formatAmount = (amount, currency) => {
        return `${amount} ${currency}`
    }

    const formatDateOnly = dateValue => {
        if (!dateValue) return '-'

        const datePart = String(dateValue).split('T')[0]
        const [year, month, day] = datePart.split('-').map(Number)

        if (!year || !month || !day) return datePart

        return new Date(year, month - 1, day).toLocaleDateString('en-US', {
            month: 'long',
            day: 'numeric',
            year: 'numeric',
        })
    }

    const handleDialogClose = () => {
        setShowSucDialog(false)
        setSuccessDialog({ open: false, message: '' })
        setShowErrDialog(false)
        setErrorDialog({ open: false, message: '' })
    }

    const handleActivate = (id, name) => {
        setActivateDialog({
            open: true,
            id,
            name,
        })
    }

    const handleDeactivate = (id, name) => {
        setDeactivateDialog({
            open: true,
            id,
            name,
        })
    }

    const confirmActivate = () => {
        if (activateDialog.id) {
            activateSubscription({
                variables: { subscription_id: activateDialog.id },
            })
        }
    }

    const confirmDeactivate = () => {
        if (deactivateDialog.id) {
            deactivateSubscription({
                variables: { subscription_id: deactivateDialog.id },
            })
        }
    }


    return (
        <>
            <div className="mb-6 flex justify-between items-center">
                <button
                    onClick={() => navigate('/')}
                    type="button"
                    className="inline-flex items-center gap-x-1.5 rounded-md text-sm"
                >
                    <ArrowLeftIcon aria-hidden="true" className="-ml-0.5 h-5 w-5 rtl:rotate-180" />
                    {t('Home')}
                </button>
            </div>

            <div className="flex flex-wrap items-end justify-between gap-4">
                <div className="max-sm:w-full sm:flex-1">
                    <div>
                        <div className="flex flex-wrap items-center gap-6 sm:flex-nowrap justify-between">
                            <div className="flex flex-wrap items-center gap-6 sm:flex-nowrap">
                                <h1 className="text-base font-semibold leading-7 text-gray-900 flex items-center">
                                    <div className="mx-auto flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full sm:mx-0 sm:h-10 sm:w-10 bg-[#e9eff1] w-[40px] h-[40px]">
                                        <TicketIcon className="w-[20px] h-[20px] fill-[#285f74]" />
                                    </div>
                                    <div>
                                        <span className="ms-3">{t('Manage Subscription Plans')}</span>
                                        <p className="ms-3 text-sm font-normal leading-6 weight-[200] text-gray-500">
                                            {t('Easily view, add, and edit subscription plans for your customers.')}
                                        </p>
                                    </div>
                                </h1>
                            </div>
                            <div className="flex flex-wrap items-center gap-6 sm:flex-nowrap sm:text-end">
                                <button
                                    onClick={() => navigate('/subscriptions/create')}
                                    type="button"
                                    className="hover:bg-primary/[.9] bg-primary focus-visible:outline-primary justify-center rounded-md px-3 py-1.5 text-sm font-semibold leading-6 text-white shadow-sm focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2"
                                >
                                    {t('Add Subscription')}
                                </button>
                            </div>
                        </div>
                    </div>
                    <div className="mt-8 mb-4 flex flex-col sm:flex-row flex-wrap max-w-xl gap-4">
                        <div className="flex-2">
                            <InputGroup>
                                <MagnifyingGlassIcon />
                                <Input
                                    value={searchString}
                                    onChange={e => setSearchString(e.target.value)}
                                    name="search"
                                    id="search"
                                    placeholder={t('Search subscriptions')}
                                />
                            </InputGroup>
                        </div>

                        <div className="flex gap-2 justify-end">
                            <button
                                onClick={onClickSearch}
                                type="button"
                                className="inline-flex items-center justify-center rounded-md bg-primary px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-primary/[0.9]"
                            >
                                {t('Search')}
                            </button>
                            <button
                                onClick={onClickSearchClear}
                                type="button"
                                className="inline-flex items-center justify-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50"
                            >
                                {t('Clear')}
                            </button>
                        </div>
                    </div>
                    <div className="mt-4 mb-4 flex flex-col sm:flex-row flex-wrap gap-4">
                        <div className="flex-1">
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                {t('Status')}
                            </label>
                            <Select value={statusFilter} onChange={e => setStatusFilter(e.target.value)}>
                                <option value="">{t('All Statuses')}</option>
                                <option value="ACT">{t('Active')}</option>
                                <option value="DRF">{t('Draft')}</option>
                                <option value="EXP">{t('Expired')}</option>
                                <option value="INA">{t('Inactive')}</option>
                            </Select>
                        </div>
                        <div className="flex-1">
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                {t('Category')}
                            </label>
                            <Select value={categoryFilter} onChange={e => setCategoryFilter(e.target.value)}>
                                <option value="">{t('All Categories')}</option>
                                <option value="DMC">{t('Domestic')}</option>
                                <option value="INL">{t('International')}</option>
                            </Select>
                        </div>
                    </div>
                </div>
            </div>
            <Divider />

            {loading && (
                <div className="flex flex-col justify-center items-center bg-white h-96 mt-16">
                    <img alt="logo" src={loadingImage} className="w-[250px]" />
                    <p className="pt-3 text-primary font-semibold">Loading, please wait...</p>
                </div>
            )}
            {error && (
                <div className="flex justify-center items-center bg-white py-5">
                    <div className="text-lg text-center">{t('Failed to fetch subscriptions!')}</div>
                </div>
            )}

            {!loading && !error && filteredSubscriptions.length === 0 && (
                <div className="text-center py-24 bg-gray-50 mt-8 rounded-lg">
                    <svg
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                        aria-hidden="true"
                        className="mx-auto h-12 w-12 text-gray-400"
                    >
                        <path
                            d="M16.6725 16.6412L21 21M19 11C19 15.4183 15.4183 19 11 19C6.58172 19 3 15.4183 3 11C3 6.58172 6.58172 3 11 3C15.4183 3 19 6.58172 19 11Z"
                            strokeWidth={2}
                            vectorEffect="non-scaling-stroke"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                        />
                    </svg>
                    <h3 className="mt-2 text-sm font-semibold text-gray-900">{t('No data found!')}</h3>
                    <p className="mt-1 text-sm text-gray-500">{t('Try adjusting your search criteria')}</p>
                    <div className="mt-6"></div>
                </div>
            )}
            {!loading && !error && filteredSubscriptions.length > 0 && (
                <>
                    <div className="table-custom rounded-b-lg bg-white p-0 flex">
                        <Table className="[--gutter:theme(spacing.6)] lg:[--gutter:theme(spacing.10)] w-full">
                            <TableHead>
                                <TableRow>
                                    <TableHeader>{t('Plan Name')}</TableHeader>
                                    <TableHeader>{t('Amount')}</TableHeader>
                                    <TableHeader>{t('Period')}</TableHeader>
                                    <TableHeader>{t('Category')}</TableHeader>
                                    <TableHeader>{t('Status')}</TableHeader>
                                    <TableHeader>{t('Valid From')}</TableHeader>
                                    <TableHeader>{t('Valid To')}</TableHeader>
                                    <TableHeader>{t('Actions')}</TableHeader>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {filteredSubscriptions.map(subscription => (
                                    <TableRow
                                        key={subscription.subscription_id}
                                        className="cursor-pointer hover:bg-gray-50"
                                        onClick={() => navigate(`/subscriptions/edit/${subscription.subscription_id}`)}
                                        title={`Subscription #${subscription.subscription_id}`}
                                    >
                                        <TableCell className="min-w-[200px]">
                                            <div>
                                                <div className="font-medium text-gray-900">
                                                    {subscription.subscription_name?.en}
                                                </div>
                                                {subscription.description?.en && (
                                                    <div className="text-sm text-gray-500 line-clamp-1">
                                                        {subscription.description?.en}
                                                    </div>
                                                )}
                                            </div>
                                        </TableCell>
                                        <TableCell className="min-w-[100px]">
                                            <span className="font-semibold text-primary">
                                                {formatAmount(subscription.amount, subscription.currency)}
                                            </span>
                                        </TableCell>
                                        <TableCell className="min-w-[120px]">
                                            {subscription.period?.description?.en || subscription.period_code}
                                        </TableCell>
                                        <TableCell className="min-w-[100px]">
                                            {getCategoryBadge(subscription.category_code)}
                                        </TableCell>
                                        <TableCell className="min-w-[100px]">
                                            {getStatusBadge(subscription.status_code)}
                                        </TableCell>
                                        <TableCell className="min-w-[100px]">
                                            {formatDateOnly(subscription.valid_from)}
                                        </TableCell>
                                        <TableCell className="min-w-[100px]">
                                            {formatDateOnly(subscription.valid_to)}
                                        </TableCell>
                                        <TableCell>
                                            <Dropdown>
                                                <DropdownButton
                                                    plain
                                                    aria-label="More options"
                                                    onClick={e => e.stopPropagation()}
                                                    className="cursor-pointer"
                                                >
                                                    <EllipsisVerticalIcon />
                                                </DropdownButton>
                                                <DropdownMenu anchor="bottom">
                                                    <DropdownItem
                                                        onClick={e => {
                                                            e.stopPropagation()
                                                            navigate(`/subscriptions/edit/${subscription.subscription_id}`)
                                                        }}
                                                    >
                                                        <EyeIcon />
                                                        <DropdownLabel>{t('View')}</DropdownLabel>
                                                    </DropdownItem>
                                                    <DropdownDivider />
                                                    <DropdownItem
                                                        onClick={e => {
                                                            e.stopPropagation()
                                                            navigate(`/subscriptions/edit/${subscription.subscription_id}`)
                                                        }}
                                                    >
                                                        <PencilIcon />
                                                        <DropdownLabel>{t('Edit')}</DropdownLabel>
                                                    </DropdownItem>
                                                    {subscription.status_code !== 'ACT' && (
                                                        <>
                                                            <DropdownDivider />
                                                            <DropdownItem
                                                                onClick={e => {
                                                                    e.stopPropagation()
                                                                    handleActivate(
                                                                        subscription.subscription_id,
                                                                        subscription.subscription_name?.en
                                                                    )
                                                                }}
                                                            >
                                                                <PlusIcon />
                                                                <DropdownLabel>{t('Activate')}</DropdownLabel>
                                                            </DropdownItem>
                                                        </>
                                                    )}
                                                    {subscription.status_code === 'ACT' && (
                                                        <>
                                                            <DropdownDivider />
                                                            <DropdownItem
                                                                onClick={e => {
                                                                    e.stopPropagation()
                                                                    handleDeactivate(
                                                                        subscription.subscription_id,
                                                                        subscription.subscription_name?.en
                                                                    )
                                                                }}
                                                            >
                                                                <TrashIcon />
                                                                <DropdownLabel>{t('Deactivate')}</DropdownLabel>
                                                            </DropdownItem>
                                                        </>
                                                    )}
                                                </DropdownMenu>
                                            </Dropdown>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </div>
                    <Divider />
                    {totalPages > 1 && (
                        <Pagination className="py-6">
                            <PaginationPrevious
                                onClick={() => handlePageChange(page > 0 ? page - 1 : 0)}
                                isDisabled={page === 0}
                            />
                            <PaginationList>
                                {getPageRange(page, totalPages).map(index => (
                                    <PaginationPage
                                        key={index}
                                        current={index === page}
                                        onClick={() => handlePageChange(index)}
                                    >
                                        {index + 1}
                                    </PaginationPage>
                                ))}
                            </PaginationList>
                            <PaginationNext
                                onClick={() => handlePageChange(page < totalPages - 1 ? page + 1 : totalPages - 1)}
                                isDisabled={page === totalPages - 1}
                            />
                        </Pagination>
                    )}
                    <Divider />
                </>
            )}


            {showErrDialog && (
                <ErrorDialog status={true} title={t('Error')} message={errorDialog.message} onClose={handleDialogClose} />
            )}
            {showSucDialog && (
                <SuccessDialog status={true} title={t('Success')} message={successDialog.message} onClose={handleDialogClose} />
            )}
            {activateDialog.open && (
                <ConfirmDialog
                    status={true}
                    title={t('Activate Subscription Plan')}
                    message={`${t('Are you sure you want to activate')} ${activateDialog.name}?`}
                    onPositive={confirmActivate}
                    onNegative={() => setActivateDialog({ open: false, id: null, name: '' })}
                    positiveActionText={activateLoading ? t('Activating...') : t('Activate')}
                    negativeActionText={t('Cancel')}
                />
            )}
            {deactivateDialog.open && (
                <ConfirmDialog
                    status={true}
                    title={t('Deactivate Subscription Plan')}
                    message={`${t('Are you sure you want to deactivate')} ${deactivateDialog.name}?`}
                    onPositive={confirmDeactivate}
                    onNegative={() => setDeactivateDialog({ open: false, id: null, name: '' })}
                    positiveActionText={deactivateLoading ? t('Deactivating...') : t('Deactivate')}
                    negativeActionText={t('Cancel')}
                />
            )}
        </>
    )
}

export default SubscriptionManagement
